import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  message,
  Spin,
  Popconfirm,
} from 'antd';
import {
  ReloadOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import { useResponsive } from '@/shared/hooks/useResponsive';
import { siteApiService } from '@/features/sites/services/siteApiService';
import type { Site, Panel, PanelTransferReason } from '@/features/sites/types/site.types';
import { TRANSFER_REASON_OPTIONS } from '@/features/sites/types/site.types';

const { Title, Text } = Typography;

/** 現場フォームの値 */
interface SiteFormValues {
  siteName: string;
}

/** 盤フォームの値 */
interface PanelFormValues {
  panelName: string;
}

/** 盤移動フォームの値 */
interface TransferFormValues {
  targetSiteId: number;
  transferReason: PanelTransferReason;
  note?: string;
}

export const SiteManagementPage: React.FC = () => {
  const { isMobile } = useResponsive();
  const [sites, setSites] = useState<readonly Site[]>([]);
  const [panels, setPanels] = useState<readonly Panel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const selectedSiteIdRef = useRef<number | null>(null);
  selectedSiteIdRef.current = selectedSiteId;

  // モーダル状態
  const [isSiteModalVisible, setIsSiteModalVisible] = useState(false);
  const [isPanelModalVisible, setIsPanelModalVisible] = useState(false);
  const [isTransferModalVisible, setIsTransferModalVisible] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [editingPanel, setEditingPanel] = useState<Panel | null>(null);
  const [transferringPanel, setTransferringPanel] = useState<Panel | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // フォーム
  const [siteForm] = Form.useForm<SiteFormValues>();
  const [panelForm] = Form.useForm<PanelFormValues>();
  const [transferForm] = Form.useForm<TransferFormValues>();

  // データ取得
  const loadData = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);
      const [sitesData, panelsData] = await Promise.all([
        siteApiService.getAllSites(),
        siteApiService.getAllPanels(),
      ]);
      setSites(sitesData);
      setPanels(panelsData);

      // 選択中の現場が削除されていた場合は最初の現場を選択
      const currentSelectedSiteId = selectedSiteIdRef.current;
      if (currentSelectedSiteId !== null) {
        const exists = sitesData.some((s) => s.id === currentSelectedSiteId);
        if (!exists && sitesData.length > 0) {
          setSelectedSiteId(sitesData[0].id);
        } else if (!exists) {
          setSelectedSiteId(null);
        }
      } else if (sitesData.length > 0) {
        setSelectedSiteId(sitesData[0].id);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(`データの取得に失敗しました: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 選択中の現場に紐づく盤をフィルタ
  const filteredPanels = panels.filter((panel) => panel.siteId === selectedSiteId);

  // ========== 現場 CRUD ==========

  const handleAddSite = (): void => {
    setEditingSite(null);
    siteForm.resetFields();
    setIsSiteModalVisible(true);
  };

  const handleEditSite = (site: Site): void => {
    setEditingSite(site);
    siteForm.setFieldsValue({ siteName: site.siteName });
    setIsSiteModalVisible(true);
  };

  const handleSiteSubmit = async (): Promise<void> => {
    try {
      const values = await siteForm.validateFields();
      setIsSubmitting(true);

      if (editingSite !== null) {
        await siteApiService.updateSite({
          siteId: editingSite.id,
          siteName: values.siteName,
        });
        message.success('現場を更新しました');
      } else {
        await siteApiService.createSite({ siteName: values.siteName });
        message.success('現場を作成しました');
      }

      setIsSiteModalVisible(false);
      siteForm.resetFields();
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSite = async (siteId: number): Promise<void> => {
    try {
      await siteApiService.deleteSite({ siteId });
      message.success('現場を削除しました');
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(errorMessage);
    }
  };

  // ========== 盤 CRUD ==========

  const handleAddPanel = (): void => {
    if (selectedSiteId === null) {
      message.warning('現場を選択してください');
      return;
    }
    setEditingPanel(null);
    panelForm.resetFields();
    setIsPanelModalVisible(true);
  };

  const handleEditPanel = (panel: Panel): void => {
    setEditingPanel(panel);
    panelForm.setFieldsValue({
      panelName: panel.panelName,
    });
    setIsPanelModalVisible(true);
  };

  const handlePanelSubmit = async (): Promise<void> => {
    if (selectedSiteId === null) {
      return;
    }

    try {
      const values = await panelForm.validateFields();
      setIsSubmitting(true);

      if (editingPanel !== null) {
        await siteApiService.updatePanel({
          panelId: editingPanel.id,
          panelName: values.panelName,
        });
        message.success('盤を更新しました');
      } else {
        await siteApiService.createPanel({
          panelName: values.panelName,
          siteId: selectedSiteId,
        });
        message.success('盤を作成しました');
      }

      setIsPanelModalVisible(false);
      panelForm.resetFields();
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePanel = async (panelId: number): Promise<void> => {
    try {
      await siteApiService.deletePanel({ panelId });
      message.success('盤を削除しました');
      await loadData();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      message.error(errorMessage);
    }
  };

  // ========== 盤移動 ==========

  const handleTransferPanel = (panel: Panel): void => {
    setTransferringPanel(panel);
    transferForm.resetFields();
    setIsTransferModalVisible(true);
  };

  const handleTransferSubmit = async (): Promise<void> => {
    if (transferringPanel === null) {
      return;
    }

    try {
      const values = await transferForm.validateFields();
      setIsSubmitting(true);

      // 確認ダイアログを表示
      const targetSite = sites.find((s) => s.id === values.targetSiteId);
      const targetSiteName = targetSite?.siteName ?? '';

      Modal.confirm({
        title: '盤を移動しますか？',
        content: (
          <div>
            <p>盤「{transferringPanel.panelName}」を「{targetSiteName}」に移動しますか？</p>
            <Alert
              message="移動後、この盤は移動先現場のユーザーのみ閲覧可能になります。過去のデータは履歴として保持されます。"
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
            />
          </div>
        ),
        okText: '移動する',
        cancelText: 'キャンセル',
        onOk: async () => {
          try {
            const result = await siteApiService.transferPanel({
              panelId: transferringPanel.id,
              targetSiteId: values.targetSiteId,
              transferReason: values.transferReason,
              note: values.note,
            });
            message.success(result.message);
            setIsTransferModalVisible(false);
            transferForm.resetFields();
            setTransferringPanel(null);
            await loadData();
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : '盤の移動に失敗しました';
            message.error(errorMessage);
          }
        },
      });
    } catch {
      // バリデーションエラー（validateFieldsの失敗）
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTransferCancel = (): void => {
    setIsTransferModalVisible(false);
    transferForm.resetFields();
    setTransferringPanel(null);
  };

  // 移動先現場の選択肢（現在の現場を除く）
  const getTransferSiteOptions = (): Array<{ value: number; label: string }> => {
    if (transferringPanel === null) {
      return [];
    }
    return sites
      .filter((s) => s.id !== transferringPanel.siteId)
      .map((s) => ({ value: s.id, label: s.siteName }));
  };

  // 現場テーブルカラム
  const siteColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '現場名',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (name: string, record: Site) => (
        <Button
          type="link"
          onClick={() => setSelectedSiteId(record.id)}
          style={{
            fontWeight: selectedSiteId === record.id ? 'bold' : 'normal',
            padding: 0,
          }}
        >
          <EnvironmentOutlined style={{ marginRight: 4 }} />
          {name}
        </Button>
      ),
    },
    {
      title: '盤数',
      key: 'panelCount',
      render: (_: unknown, record: Site) => {
        const count = panels.filter((p) => p.siteId === record.id).length;
        return (
          <Tag color={count > 0 ? 'blue' : 'default'}>
            {count} 台
          </Tag>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: Site) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditSite(record)}
          />
          <Popconfirm
            title="現場を削除しますか？"
            description="紐づく盤がある場合は削除できません"
            onConfirm={() => handleDeleteSite(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 盤テーブルカラム
  const panelColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: '盤名',
      dataIndex: 'panelName',
      key: 'panelName',
      render: (name: string) => (
        <Space>
          <AppstoreOutlined />
          {name}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: Panel) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditPanel(record)}
          />
          <Button
            type="link"
            size="small"
            icon={<SwapOutlined />}
            onClick={() => handleTransferPanel(record)}
            title="別の現場に移動"
          />
          <Popconfirm
            title="盤を削除しますか？"
            description="関連データも削除されます"
            onConfirm={() => handleDeletePanel(record.id)}
            okText="削除"
            cancelText="キャンセル"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 選択中の現場名を取得
  const selectedSiteName = sites.find((s) => s.id === selectedSiteId)?.siteName ?? '';

  return (
    <div>
      <div className={isMobile ? 'mb-4' : 'flex justify-between items-center mb-6'}>
        <Title level={2} style={isMobile ? { marginBottom: '12px' } : undefined}>
          現場・盤管理
        </Title>
        <Space
          direction={isMobile ? 'vertical' : 'horizontal'}
          style={isMobile ? { width: '100%' } : undefined}
        >
          <Button
            icon={<ReloadOutlined />}
            onClick={loadData}
            loading={isLoading}
            block={isMobile}
          >
            更新
          </Button>
        </Space>
      </div>

      {isLoading ? (
        <div className="text-center py-8">
          <Spin size="large" tip="読み込み中..." />
        </div>
      ) : (
        <div className="flex flex-col xl:flex-row gap-4">
          {/* 現場一覧 */}
          <Card
            title={
              <Space>
                <EnvironmentOutlined />
                <span>現場一覧</span>
                <Tag color="blue">{sites.length} 件</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddSite}
              >
                追加
              </Button>
            }
            className="flex-1"
          >
            <Table
              columns={siteColumns}
              dataSource={sites.map((site) => ({ ...site, key: site.id }))}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              rowClassName={(record) =>
                record.id === selectedSiteId ? 'ant-table-row-selected' : ''
              }
            />
          </Card>

          {/* 盤一覧 */}
          <Card
            title={
              <Space>
                <AppstoreOutlined />
                <span>{selectedSiteName ? `${selectedSiteName} の盤` : '盤一覧'}</span>
                <Tag color="green">{filteredPanels.length} 台</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddPanel}
                disabled={selectedSiteId === null}
              >
                追加
              </Button>
            }
            className="flex-1"
          >
            {selectedSiteId === null ? (
              <Text type="secondary">左の現場を選択してください</Text>
            ) : filteredPanels.length === 0 ? (
              <Text type="secondary">この現場には盤が登録されていません</Text>
            ) : (
              <Table
                columns={panelColumns}
                dataSource={filteredPanels.map((panel) => ({ ...panel, key: panel.id }))}
                pagination={false}
                size="small"
                scroll={{ x: 'max-content' }}
              />
            )}
          </Card>
        </div>
      )}

      {/* 現場モーダル */}
      <Modal
        title={editingSite !== null ? '現場を編集' : '現場を追加'}
        open={isSiteModalVisible}
        onOk={handleSiteSubmit}
        onCancel={() => setIsSiteModalVisible(false)}
        okText={editingSite !== null ? '更新' : '作成'}
        cancelText="キャンセル"
        confirmLoading={isSubmitting}
      >
        <Form form={siteForm} layout="vertical">
          <Form.Item
            name="siteName"
            label="現場名"
            rules={[
              { required: true, message: '現場名を入力してください' },
              { max: 100, message: '現場名は100文字以内で入力してください' },
            ]}
          >
            <Input placeholder="現場名を入力" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 盤モーダル */}
      <Modal
        title={editingPanel !== null ? '盤を編集' : '盤を追加'}
        open={isPanelModalVisible}
        onOk={handlePanelSubmit}
        onCancel={() => setIsPanelModalVisible(false)}
        okText={editingPanel !== null ? '更新' : '作成'}
        cancelText="キャンセル"
        confirmLoading={isSubmitting}
      >
        <Form form={panelForm} layout="vertical">
          <Form.Item
            name="panelName"
            label="盤名"
            rules={[
              { required: true, message: '盤名を入力してください' },
              { max: 100, message: '盤名は100文字以内で入力してください' },
            ]}
          >
            <Input placeholder="盤名を入力" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 盤移動モーダル */}
      <Modal
        title="盤を移動"
        open={isTransferModalVisible}
        onOk={handleTransferSubmit}
        onCancel={handleTransferCancel}
        okText="移動する"
        cancelText="キャンセル"
        confirmLoading={isSubmitting}
      >
        {transferringPanel !== null && (
          <Form form={transferForm} layout="vertical">
            <div style={{ marginBottom: 16 }}>
              <strong>対象盤:</strong> {transferringPanel.panelName}
              （現在: {sites.find((s) => s.id === transferringPanel.siteId)?.siteName ?? '不明'}）
            </div>

            <Form.Item
              name="targetSiteId"
              label="移動先現場"
              rules={[{ required: true, message: '移動先現場を選択してください' }]}
            >
              <Select
                placeholder="現場を選択"
                options={getTransferSiteOptions()}
              />
            </Form.Item>

            <Form.Item
              name="transferReason"
              label="移動理由"
              rules={[{ required: true, message: '移動理由を選択してください' }]}
            >
              <Select
                placeholder="理由を選択"
                options={TRANSFER_REASON_OPTIONS.map((opt) => ({
                  value: opt.value,
                  label: opt.label,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="note"
              label="備考"
              rules={[{ max: 1000, message: '備考は1000文字以内で入力してください' }]}
            >
              <Input.TextArea
                placeholder="備考を入力（任意）"
                rows={3}
              />
            </Form.Item>

            <Alert
              message="移動すると、この盤は移動先現場のユーザーのみ閲覧可能になります"
              type="warning"
              showIcon
            />
          </Form>
        )}
      </Modal>
    </div>
  );
};
